// Port of the prototype's reel animation, decoupled from prize selection.
// The server tells us the outcome (win/lose + which prize); this module makes
// the reels actually LAND on that outcome.

import type { PaytablePrize } from './api';

export const SYM_H = 130; // px per symbol — must match CSS drum height (390px = 3 symbols)
export const STRIP_LEN = 150; // symbols per reel
export const STOP_TIMES = [1800, 2350, 2900] as const; // ms — left to right staggered stop
export const POST_STOP_PAUSE = 1200; // ms before showing result popup

export interface ReelStripEntry {
  prizeId: number | null;
  emoji: string;
  image_url: string | null;
  name: string;
}

// Build a deterministic strip from the active paytable. 150 symbols, cycling
// through the prize pool. Identical strips on all 3 reels keeps animation simple.
export function buildStrip(pool: PaytablePrize[]): ReelStripEntry[] {
  const out: ReelStripEntry[] = [];
  if (pool.length === 0) return out;
  for (let i = 0; i < STRIP_LEN; i++) {
    const p = pool[i % pool.length];
    out.push({ prizeId: p.id, emoji: p.emoji, image_url: p.image_url, name: p.name });
  }
  return out;
}

// Find an index on the strip where the symbol matches the target prize, in the
// landing window (40% onward, capped to leave 2 symbols below visible).
export function findSymIndex(strip: ReelStripEntry[], targetPrizeId: number): number {
  const start = Math.floor(strip.length * 0.4);
  const end = strip.length - 3;
  for (let i = start; i <= end; i++) {
    if (strip[i].prizeId === targetPrizeId) return i;
  }
  // Fallback: search from beginning.
  for (let i = 0; i <= end; i++) {
    if (strip[i].prizeId === targetPrizeId) return i;
  }
  return Math.floor(strip.length / 2);
}

// For no-win reels: pick an index whose symbol differs from `excludePrizeId`.
export function findNonMatchIndex(strip: ReelStripEntry[], excludePrizeId: number): number {
  const start = Math.floor(strip.length * 0.4);
  const end = strip.length - 3;
  for (let attempt = 0; attempt < 20; attempt++) {
    const idx = start + Math.floor(Math.random() * (end - start));
    if (strip[idx].prizeId !== excludePrizeId) return idx;
  }
  return start;
}

// rAF-driven single-phase eased animation. Drives `setTop(px)` from -SYM_H
// to `-landIdx * SYM_H` over `duration` ms.
export function animateReel(
  durationMs: number,
  landIdx: number,
  setTop: (px: number) => void,
  onDone: () => void,
): () => void {
  const startTime = performance.now();
  const endTop = -landIdx * SYM_H;
  const startTop = -SYM_H;
  let cancelled = false;
  let rafId = 0;

  function easeOutQuart(t: number) {
    return 1 - Math.pow(1 - t, 4);
  }

  function frame(now: number) {
    if (cancelled) return;
    const t = Math.min(1, (now - startTime) / durationMs);
    const eased = easeOutQuart(t);
    setTop(startTop + (endTop - startTop) * eased);
    if (t < 1) {
      rafId = requestAnimationFrame(frame);
    } else {
      onDone();
    }
  }
  setTop(startTop);
  rafId = requestAnimationFrame(frame);
  return () => {
    cancelled = true;
    cancelAnimationFrame(rafId);
  };
}
